import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  AlertTriangle, Search, Shield, Users,
  DollarSign, Calendar, Eye
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { intelligenceAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const RetailerRiskPage = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [retailers, setRetailers] = useState([]);
  const [filterLevel, setFilterLevel] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchRetailers();
    }
  }, [activeBusiness?.id, filterLevel]);

  const fetchRetailers = async () => {
    try {
      setLoading(true);
      const params = filterLevel !== 'ALL' ? { level: filterLevel } : {};
      const { data } = await intelligenceAPI.getRetailerRisks(params);
      setRetailers(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load retailer risks'));
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'CRITICAL': return 'bg-rose-50 text-rose-700';
      case 'HIGH': return 'bg-amber-50 text-amber-700';
      case 'MEDIUM': return 'bg-yellow-50 text-yellow-700';
      default: return 'bg-emerald-50 text-emerald-700';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString()}`;
  };

  const criticalCount = retailers.filter(r => r.risk_category === 'CRITICAL').length;
  const highCount = retailers.filter(r => r.risk_category === 'HIGH').length;
  const totalOutstanding = retailers.reduce((sum, r) => sum + (r.factors?.unpaid_amount || 0), 0);

  const filteredRetailers = retailers.filter(retailer =>
    !searchQuery ||
    retailer.retailer_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col" data-testid="retailer-risk-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Retailer Risk Analysis"
        subtitle="Monitor high-risk retailers and payment patterns"
        stats={[
          { icon: Users, label: 'Total Retailers', value: retailers.length, color: 'slate' },
          { icon: AlertTriangle, label: 'Critical Risk', value: criticalCount, color: 'rose' },
          { icon: Shield, label: 'High Risk', value: highCount, color: 'amber' },
          { icon: DollarSign, label: 'Total Outstanding', value: formatCurrency(totalOutstanding), color: 'purple' },
        ]}
        secondaryAction={{
          label: 'Overview',
          icon: Eye,
          onClick: () => navigate(`/business/${activeBusiness?.id}/intelligence/overview`)
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search retailers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
            <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200">
              {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((level) => (
                <button
                  key={level}
                  onClick={() => setFilterLevel(level)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                    filterLevel === level
                      ? 'bg-white text-teal-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading retailer data...</p>
              </div>
            </div>
          ) : filteredRetailers.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No retailers found</p>
              <p className="text-[13px] text-slate-400 mt-1">No retailers match your current filters</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Retailer</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Risk Level</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white text-right">Outstanding</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Last Visit</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Risk Score</th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredRetailers.map((retailer, idx) => (
                      <tr
                        key={retailer.party_id}
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn cursor-pointer ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                        onClick={() => navigate(`/business/${activeBusiness?.id}/customers/${retailer.party_id}`)}
                      >
                        <td className="tl-td">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold ${getInitialColor(retailer.retailer_name)}`}>
                              {getInitials(retailer.retailer_name)}
                            </div>
                            <div>
                              <p className="text-[13px] font-semibold text-slate-800">{retailer.retailer_name}</p>
                              <p className="text-[11px] text-slate-500">{retailer.phone || retailer.city || 'No contact'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="tl-td">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium ${getRiskColor(retailer.risk_category)}`}>
                            {retailer.risk_category === 'CRITICAL' && <AlertTriangle className="w-3 h-3" />}
                            {retailer.risk_category}
                          </span>
                        </td>
                        <td className="tl-td text-right">
                          <div className="text-[13px] font-semibold text-slate-800">
                            {formatCurrency(retailer.factors?.unpaid_amount)}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {(retailer.factors?.days_since_last_visit || 0) > 0
                              ? `${retailer.factors.days_since_last_visit} days since visit`
                              : 'Recent'}
                          </div>
                        </td>
                        <td className="tl-td">
                          <div className="flex items-center gap-1.5 text-[13px] text-slate-600">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            {(retailer.factors?.visit_frequency_30d || 0) > 0
                              ? `${retailer.factors.visit_frequency_30d} in 30d`
                              : 'No visits'}
                          </div>
                        </td>
                        <td className="tl-td">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${getRiskColor(retailer.risk_category)}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              retailer.risk_category === 'CRITICAL' ? 'bg-rose-500' :
                              retailer.risk_category === 'HIGH' ? 'bg-amber-500' :
                              retailer.risk_category === 'MEDIUM' ? 'bg-yellow-500' : 'bg-emerald-500'
                            }`}></span>
                            {retailer.overall_score?.toFixed(0) || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredRetailers.length}</span> of <span className="font-medium text-slate-700">{retailers.length}</span> retailers
                </p>
                <p className="text-[11px] text-slate-400">Click on a row to view retailer details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetailerRiskPage;
